/**
 * Auth handlers (auth:*).
 *
 * In addition to writing through the AuthAdapter, we mirror the new
 * credentials into the `@session` state slot so flows that read
 * `@session.accessToken` (or `@session.userId`) right after `auth:write`
 * see the freshly-written value.
 *
 * Two sync targets per write:
 *1. ctx.state.set("session", ...) → notifies the renderer's
 * external store subscribers (so the UI re-renders with the new
 * session data).
 *2. ctx.setSession(...) → updates the per-execution `session`
 * snapshot so `@session.*` reads inside the same flow resolve
 * to the freshly written value without round-tripping through
 * the store.
 */

import type { FlowStep } from "../types/flow";
import type { AuthCredentials, ExecutionContext } from "../context/execution-context";
import { resolveValue } from "../expressions/resolve";
import { registerStepHandler } from "../runtime/step-registry";

function asRecord(step: FlowStep): Record<string, unknown> {
 return step as unknown as Record<string, unknown>;
}

function pushSessionState(ctx: ExecutionContext, creds: AuthCredentials | null): void {
 if (creds) {
 const asObject = creds as unknown as Record<string, unknown>;
 ctx.state.set("session", { ...asObject });
 ctx.setSession(asObject);
 } else {
 ctx.state.set("session", {});
 ctx.setSession(null);
 }
}

registerStepHandler("auth:write", (step, ctx) => {
 const r = asRecord(step);
 const userId = String(resolveValue(r["userId"], ctx) ?? "");
 const accessToken = String(resolveValue(r["accessToken"], ctx) ?? "");
 if (!userId || !accessToken) {
 ctx.logger.warn(`auth:write skipped: missing userId or accessToken`);
 return;
 }

 const refreshToken = resolveValue(r["refreshToken"], ctx);
 const isGuest = resolveValue(r["isGuest"], ctx);
 const expiresAt = resolveValue(r["expiresAt"], ctx);

 const creds: AuthCredentials = {
 userId,
 accessToken,
 ...(typeof refreshToken === "string" ? { refreshToken } : {}),
 ...(typeof isGuest === "boolean" ? { isGuest } : {}),
 ...(typeof expiresAt === "number" ? { expiresAt } : {}),
 };

 if (ctx.auth) ctx.auth.write(creds);
 pushSessionState(ctx, creds);
});

registerStepHandler("auth:clear", (_, ctx) => {
 if (ctx.auth) ctx.auth.clear();
 pushSessionState(ctx, null);
});